import immer from 'immer';
import type { ApolloError } from '@apollo/client';
import { getApolloClient, readFieldIfReference } from '@frameio/graphql-client';
import type { Account } from '@frameio/graphql-client/generated';
import { WorkspacesInfoProject } from '@frameio/graphql-client/generated';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { useTrack } from '@frameio/shared/segment/hooks';
import { reportError } from '@frameio/telemetry';
import { useToast } from '@frameio/vapor';
import { Toast, TOAST_OPTIONS } from '../components/Toast';
import type {
  EnableWorkspaceNotificationsMutationOptions,
  EnableWorkspaceNotificationsMutationVariables,
} from '../gql';
import {
  ProjectNotificationsOverrideStatus,
  useDisableWorkspaceNotificationsMutation,
  useEnableWorkspaceNotificationsMutation,
  useWorkspaceNotificationsStatusFragment,
} from '../gql';
import TEXT from './generated/TEXT/useToggleWorkspaceNotifications';

TEXT.DEFINE({
  /** Toast message shown after toggling workspace notifications */
  title: (
    hasProjectNotificationsEnabled: boolean | null | undefined,
    workspaceName: string | undefined
  ): string => {
    const projectNotificationsEnabled = !!hasProjectNotificationsEnabled;
    return projectNotificationsEnabled
      ? `Notifications disabled for all projects in "${workspaceName}".`
      : `Notifications enabled for all projects in "${workspaceName}".`;
  },
  /** Error message when toggling workspace notifications fails */
  error: (
    hasProjectNotificationsEnabled: boolean | null | undefined
  ): string => {
    const projectNotificationsEnabled = !!hasProjectNotificationsEnabled;
    return projectNotificationsEnabled
      ? `Notifications can't be disabled at this time.`
      : `Notifications can't be enabled at this time.`;
  },
});

type ModifyCachedProjectsForWorkspace = {
  accountId: string;
  hasProjectNotificationsEnabled: boolean | null | undefined;
  workspaceId?: string;
};

/**
 * @description The `updateProjectsNotificationsEnabledInWorkspace` function
 * updates the `notificationsEnabled` field on all cached Projects under a given
 * Workspace.
 *
 * @param {string} accountId - the accountId to find all Projects belonging to
 * the Account.
 * @param {boolean} hasProjectNotificationsEnabled - whether the workspace has
 * project notifications enabled before the update.
 * @param {string} workspaceId - the workspaceId to check if the Project belongs
 * to the Workspace.
 */
function updateProjectsNotificationsEnabledInWorkspace({
  accountId,
  hasProjectNotificationsEnabled,
  workspaceId,
}: ModifyCachedProjectsForWorkspace) {
  const client = getApolloClient();

  if (!workspaceId) {
    return;
  }

  client.cache.modify<Account>({
    fields: {
      projects(value, { isReference, toReference, readField }) {
        const original = readFieldIfReference({
          fieldName: 'projects',
          from: toReference({
            __typename: 'Account',
            id: accountId,
          }),
          isReference,
          readField,
          value,
        });

        if (!original) return value;

        return immer(original, (draft) => {
          draft.nodes.forEach((project) => {
            client.cache.updateFragment(
              {
                fragment: WorkspacesInfoProject,
                fragmentName: 'WorkspacesInfoProject',
                id: isReference(project)
                  ? project.__ref
                  : client.cache.identify({
                      __typename: 'Project',
                      id: project.id,
                    }),
              },
              (fragment) => {
                if (!fragment) return null;
                if (fragment.workspace?.id !== workspaceId) return fragment;
                const updatedFragment = {
                  ...fragment,
                  notificationsEnabled: !hasProjectNotificationsEnabled,
                };
                return updatedFragment;
              }
            );
            client.cache.updateFragment(
              {
                fragment: ProjectNotificationsOverrideStatus,
                fragmentName: 'ProjectNotificationsOverrideStatus',
                id: isReference(project)
                  ? project.__ref
                  : client.cache.identify({
                      __typename: 'Project',
                      id: project.id,
                    }),
              },
              (fragment) => {
                if (!fragment) return null;
                const updatedFragment = {
                  ...fragment,
                  hasProjectNotificationsOverride:
                    !hasProjectNotificationsEnabled,
                };
                return updatedFragment;
              }
            );
          });
        });
      },
    },
    id: client.cache.identify({
      __typename: 'Account',
      id: accountId,
    }),
  });
}

type ToggleProjectNotificationsForWorkspace = {
  workspaceId?: string;
};

type ToggleWorkspaceNotificationsArgs = {
  variables: EnableWorkspaceNotificationsMutationVariables;
} & EnableWorkspaceNotificationsMutationOptions;

/**
 * @description The `useToggleWorkspaceNotifications` hook returns the
 * `toggleWorkspaceNotificationsMutation` mutation so that notifications for
 * Projects within a Workspace can be turned on or off.
 *
 * @param {string} workspaceId - the workspaceId to use to turn Project notifications on or
 * off.
 *
 * @returns {Function} `toggleWorkspaceNotificationsMutation` to turn the
 * Workspace's Projects notifications on or off.
 */
export const useToggleWorkspaceNotifications = ({
  workspaceId,
}: ToggleProjectNotificationsForWorkspace) => {
  const accountId = useActiveAccountId();
  const { addToast } = useToast();

  const [disableWorkspaceNotifications] =
    useDisableWorkspaceNotificationsMutation();
  const [enableWorkspaceNotifications] =
    useEnableWorkspaceNotificationsMutation();
  const track = useTrack();

  const { data: workspaceData } = useWorkspaceNotificationsStatusFragment({
    from: {
      __typename: 'Workspace',
      id: workspaceId ?? '',
    },
  });

  const hasProjectNotificationsEnabled =
    workspaceData?.projectNotificationsEnabled;
  const workspaceName = workspaceData?.name;

  const toggleWorkspaceNotificationsMutation = () => {
    const toggleWorkspaceNotifications = hasProjectNotificationsEnabled
      ? (args: ToggleWorkspaceNotificationsArgs) =>
          disableWorkspaceNotifications(args)
      : (args: ToggleWorkspaceNotificationsArgs) =>
          enableWorkspaceNotifications(args);
    const mutation = hasProjectNotificationsEnabled
      ? 'disableWorkspaceNotifications'
      : 'enableWorkspaceNotifications';

    if (!workspaceId) {
      return;
    }

    toggleWorkspaceNotifications({
      variables: {
        input: {
          accountId,
          workspaceId,
        },
      },
      optimisticResponse: {
        __typename: 'Mutation',
        [mutation]: {
          __typename: 'ToggleWorkspaceNotificationsPayload',
          workspace: {
            id: workspaceId,
            projectNotificationsEnabled: !hasProjectNotificationsEnabled,
            __typename: 'Workspace',
          },
        },
      },
      update: () => {
        updateProjectsNotificationsEnabledInWorkspace({
          accountId,
          hasProjectNotificationsEnabled,
          workspaceId,
        });
      },
      onCompleted: () => {
        addToast(
          <Toast
            title={TEXT.title(hasProjectNotificationsEnabled, workspaceName)}
          />,
          TOAST_OPTIONS
        );
        track('workspace-notifications-updated', {
          workspace_id: workspaceId,
          notifications_enabled: !hasProjectNotificationsEnabled,
        });
      },
      onError: (error: ApolloError) => {
        addToast(
          <Toast
            title={TEXT.error(hasProjectNotificationsEnabled)}
            variant={'error'}
          />,
          TOAST_OPTIONS
        );
        reportError(error, {
          message: 'Failed to toggle workspace notifications',
        });
      },
    });
  };

  return {
    toggleWorkspaceNotificationsMutation,
  };
};
